import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'
import enUS from './locales/en-US.json'
import zhTW from './locales/zh-TW.json'

export const SUPPORTED_LANGUAGES = ['zh-TW', 'en-US'] as const
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number]

export const LANGUAGE_STORAGE_KEY = 'wyni.lang'
export const DEFAULT_LANGUAGE: SupportedLanguage = 'zh-TW'

export const isSupportedLanguage = (value: unknown): value is SupportedLanguage =>
  typeof value === 'string' &&
  (SUPPORTED_LANGUAGES as readonly string[]).includes(value)

/**
 * Storage can throw in private-browsing modes and locked-down enterprise
 * profiles. A language preference is never worth crashing the app for.
 */
export const readStoredLanguage = (): SupportedLanguage => {
  try {
    const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY)
    return isSupportedLanguage(stored) ? stored : DEFAULT_LANGUAGE
  } catch {
    return DEFAULT_LANGUAGE
  }
}

export const storeLanguage = (language: SupportedLanguage): void => {
  try {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, language)
  } catch {
    // Preference simply will not persist. Not worth surfacing.
  }
}

i18n.use(initReactI18next).init({
  resources: {
    'en-US': { translation: enUS },
    'zh-TW': { translation: zhTW },
  },
  lng: readStoredLanguage(),
  // The key IS the source string, so there is nothing sensible to fall back
  // to — a missing entry should render the original text, not another locale.
  fallbackLng: false,
  // Keys are literal sentences. Without these, 'Stock Management: 3 items'
  // would be parsed as namespace 'Stock Management' and '已完成. 請確認' as a
  // nested path.
  keySeparator: false,
  nsSeparator: false,
  returnEmptyString: false,
  // No custom parseMissingKeyHandler: i18next's built-in missing-key path
  // already returns the key itself AND still runs interpolation on it. A
  // custom handler here would return early, before interpolation runs — and
  // because this project uses source-string-as-key, every Chinese-source
  // string is a "missing key" by design (it never appears in zh-TW.json), so
  // a custom handler would render interpolated Chinese strings with literal
  // {{placeholder}} markers left in.
  interpolation: { escapeValue: false },
})

export default i18n
